import Head from 'next/head';
import { useRouter } from 'next/router';
import SiteLink from 'components/shared/sitelink';
import classNames from 'classnames';
import { addLocaleToHref } from 'lib/utils/helpers';
import { useGlobalModules } from 'contexts';
import styles from './breadcrumbs.module.scss';

const siteUrl = process.env.NEXT_PUBLIC_SITEURL;

const toAbsoluteUrl = (path, locale) => {
  const href = addLocaleToHref(path, locale);
  return `${siteUrl}${href.endsWith('/') ? href : `${href}/`}`;
};

const getBreadcrumbSchema = (breadcrumbLinks, lastTitle, locale, asPath) => {
  if (!lastTitle) return null;

  const crumbs = breadcrumbLinks.filter(link => link.slug && link.value);

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      ...crumbs.map((link, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': toAbsoluteUrl(link.slug, locale),
          name: link.value.trim(),
        },
      })),
      {
        '@type': 'ListItem',
        position: crumbs.length + 1,
        item: {
          '@id': toAbsoluteUrl(asPath.split('?')[0], locale),
          name: lastTitle.trim(),
        },
      },
    ],
  };
};

const Breadcrumbs = ({ locale, data, links, title }) => {
  const globalModules = useGlobalModules();
  const { asPath } = useRouter();

  let breadcrumbLinks;
  let lastTitle;

  if (Array.isArray(links)) {
    breadcrumbLinks = links;
    lastTitle = title;
  } else {
    const { productSpecsCollection, title: dataTitle, breadcrumbs: manualBreadcrumbs } = data || {};
    const additionalLinkNames = [
      'Product Category',
      // 'Sub Category',
      'Product Type',
    ];

    const homeLink = globalModules?.links?.breadcrumb?.home;

    if (manualBreadcrumbs?.breadcrumbs?.length) {
      // legacy trail entries include Home as a crumb; Home is prepended automatically below
      breadcrumbLinks = manualBreadcrumbs.breadcrumbs
        .filter(link => link.slug !== homeLink?.slug)
        .map(link => ({
          slug: link.slug,
          value: link.title,
        }));
      lastTitle = manualBreadcrumbs.breadcrumbEnd || dataTitle;
    } else {
      breadcrumbLinks = additionalLinkNames.reduce((acc, name) => {
        const spec = productSpecsCollection?.items?.find(item => item.type.name.includes(name));
        if (spec) {
          acc.push({
            slug: spec.link?.link?.slug,
            value: spec.value,
          });
        }
        return acc;
      }, []);
      lastTitle = dataTitle;
    }

    breadcrumbLinks.unshift({
      slug: homeLink?.slug,
      value: homeLink?.title,
    });
  }

  const schema = getBreadcrumbSchema(breadcrumbLinks, lastTitle, locale, asPath);

  return (
    <nav
      id={data?.anchorId}
      aria-label="Breadcrumb"
      className={classNames(styles.breadcrumbsWrapper)}
    >
      {schema && (
        <Head>
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
          />
        </Head>
      )}
      {breadcrumbLinks.map(link => (
        <span key={`breadcrumb-${link.slug || ''}-${link.value || ''}`}>
          <SiteLink
            locale={locale}
            href={addLocaleToHref(link.slug, locale)}
            className={classNames(styles.breadcrumbLink, { [styles.disabled]: !link.slug })}
            aria-label={link.value}
            tabIndex={link.slug ? 0 : -1}
          >
            {link.value}
          </SiteLink>
          <span aria-hidden="true">/</span>
        </span>
      ))}
      <span className={styles.breadcrumbLast} aria-current="page">
        {lastTitle}
      </span>
    </nav>
  );
};

export default Breadcrumbs;
